// Library Methods
import { useCallback, useMemo } from "react";

// Components
import ChartCross from "src/assets/shapes/ChartCross/ChartCross";
import Circle from "src/assets/shapes/Circle/Circle";
import ColoredCircle from "src/assets/shapes/ColoredCircle/ColoredCircle";
import Dash from "src/assets/shapes/Dash/Dash";
import Square from "src/assets/shapes/Square/Square";
import Triangle from "src/assets/shapes/Triangle/Triangle";

// Constants
import { eyeTypeEnums } from "src/constants/enums";

// Types
import { ChartSpotType } from "src/services/exams/types";

const DEFAULT_CONTAINER_SIZE = 312;

type RLFastGraphType = {
  vfScreeningSpots: Array<ChartSpotType>;
  gridType: string | undefined;
  eyeType: string;
  isLiveUpdate?: boolean;
};

const RLFastGraph = ({
  vfScreeningSpots,
  gridType,
  eyeType,
  isLiveUpdate = false,
}: RLFastGraphType) => {
  const { LEFT } = eyeTypeEnums;

  const scale = useMemo(() => 1, []);
  const tickSize = useMemo(() => 6, []);

  const calculateXPaddingNumberGraph = useCallback((xFloor: number) => {
    // jumpAxis lets us jump from negative x axis to positive x axis (half of total width + line width + padding)
    const jumpAxis = 133;

    if (xFloor <= 0) {
      return 163 - xFloor * -31;
    } else {
      return xFloor * 31 + jumpAxis;
    }
  }, []);

  const calculateYPaddingNumberGraph = useCallback((yFloor: number) => {
    // jumpAxis lets us jump from negative y axis to negative y axis (half of total width + line width + padding)
    const jumpAxis = 135;

    if (yFloor >= 0) {
      return 160 - yFloor * 29;
    } else {
      return jumpAxis + yFloor * -29;
    }
  }, []);

  return (
    <main
      style={{
        height: DEFAULT_CONTAINER_SIZE,
        width: DEFAULT_CONTAINER_SIZE,
        position: "relative",
      }}
    >
      <ChartCross
        gridType={gridType}
        width={DEFAULT_CONTAINER_SIZE * scale}
        height={DEFAULT_CONTAINER_SIZE * scale}
        tickSize={tickSize * scale}
      />

      {/* Triangle to show the blind spot */}
      <div
        style={{
          position: "absolute",
          marginTop: 124,
          // For left eyes, place the triangle on the left side of the grid. For right eyes, it's the opposite.
          marginLeft: (140 + (eyeType === LEFT ? -71 : 91)) * scale,
        }}
      >
        <Triangle />
      </div>

      {/* Unseen spots */}
      {vfScreeningSpots
        .filter((spot) => !spot.seen && spot.completed)
        .map((spot, i: number) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingNumberGraph(spot.x),
                paddingTop: calculateYPaddingNumberGraph(spot.y),
              }}
            >
              <Square size={8} />
            </div>
          );
        })}

      {/* Seen spots */}
      {vfScreeningSpots
        .filter((spot) => spot.seen)
        .map((spot, i: number) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingNumberGraph(spot.x),
                paddingTop: calculateYPaddingNumberGraph(spot.y),
              }}
            >
              <Circle size={10} />
            </div>
          );
        })}

      {/* In progress spots (live update modal) */}
      {/* Display an orange circle by default or a green circle on the "current spot" */}
      {vfScreeningSpots
        .filter((spot) => !spot.completed && spot.decibelValue) // filter uncompleted spots with a valid decibelValue
        .map((spot, i: number) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingNumberGraph(spot.x),
                paddingTop: calculateYPaddingNumberGraph(spot.y),
              }}
            >
              {!isLiveUpdate ? (
                <Dash size={8} />
              ) : (
                <ColoredCircle size={8} isOrange={!spot.current} />
              )}
            </div>
          );
        })}
    </main>
  );
};

export default RLFastGraph;
